import { z } from 'zod';

/**
 * `POST /api/generate` request body. v0 supports the four legacy generation
 * tasks; the backend stub returns a deterministic placehold.co URL keyed on
 * the prompt + productUid. Real AI provider integration (BFL Flux 2 Pro)
 * lands behind the same wire shape per ADR 0002.
 */
export const GENERATE_TASKS = ['enhance', 'edit', 'match-style', 'merge'] as const;
export type GenerateTask = (typeof GENERATE_TASKS)[number];

export const PROMPT_MIN_LENGTH = 1;
export const PROMPT_MAX_LENGTH = 2000;

/**
 * Output settings on a Photo Studio generation. Defaults preserve v0
 * behavior so existing call sites without these fields keep working
 * (`original` ratio, `standard` size, batch=1).
 *
 * Wire vocabulary is user-facing (`standard` / `large` / `xl`). The
 * internal `SizeTier` type ('1x' / '2x' / '3x') in generation.ts is the
 * credit multiplier; map standard→1x, large→2x, xl→3x at the
 * provider boundary.
 */
export const OUTPUT_SIZE_TIERS = ['standard', 'large', 'xl'] as const;
export type OutputSizeTier = (typeof OUTPUT_SIZE_TIERS)[number];

export const ASPECT_RATIOS = [
  'original',
  '1:1',
  '4:3',
  '3:4',
  '16:9',
  '9:16',
  '3:2',
  '2:3',
  '21:9',
  '9:21',
] as const;
export type AspectRatio = (typeof ASPECT_RATIOS)[number];

export const BATCH_SIZE_MIN = 1;
export const BATCH_SIZE_MAX = 4;

export const GenerateRequestSchema = z
  .object({
    /** Product the generation is rooted to. Free-form to support draft uids later. */
    productUid: z.string().min(1).max(200),
    task: z.enum(GENERATE_TASKS),
    prompt: z.string().min(PROMPT_MIN_LENGTH).max(PROMPT_MAX_LENGTH),
    aspectRatio: z.enum(ASPECT_RATIOS).default('original'),
    sizeTier: z.enum(OUTPUT_SIZE_TIERS).default('standard'),
    batchSize: z.number().int().min(BATCH_SIZE_MIN).max(BATCH_SIZE_MAX).default(1),
  })
  .strict();

/**
 * Use the INPUT type so callers can omit fields that have defaults
 * (aspectRatio / sizeTier / batchSize). The OUTPUT type — what the
 * backend sees post-validation — has those fields filled in.
 */
export type GenerateRequest = z.input<typeof GenerateRequestSchema>;
export type GenerateRequestParsed = z.output<typeof GenerateRequestSchema>;

/** Credit multipliers for the three size tiers (Standard / Large / XL → 1× / 2× / 3×). */
export const SIZE_TIER_CREDIT_MULTIPLIER: Record<OutputSizeTier, number> = {
  standard: 1,
  large: 2,
  xl: 3,
};
